import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Modal } from 'components/ui/Modal'
import { Badge } from 'components/ui/Badge'
import { 
  userManagementApi, 
  rolesApi,
  type UserWithRoles, 
  type Role 
} from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { UserCheck, Shield } from 'lucide-react'

interface RoleAssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  user: UserWithRoles | null
  onSuccess: () => void
}

export function RoleAssignmentModal({ isOpen, onClose, user, onSuccess }: RoleAssignmentModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { user: currentUser } = useAuth()
  const [roles, setRoles] = useState<Role[]>([])
  const [selectedRole, setSelectedRole] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [fetchingRoles, setFetchingRoles] = useState(false)

  // Reset selected role when modal opens or user changes
  useEffect(() => {
    if (isOpen && user) {
      // Set the first role if user has roles, otherwise null
      setSelectedRole(user.roles.length > 0 ? user.roles[0].role : null)
      fetchRoles()
    }
  }, [isOpen, user, currentUser])

  const fetchRoles = async () => {
    try {
      setFetchingRoles(true)
      const response = await rolesApi.getRoles()
      const rolesData = Array.isArray(response.data) ? response.data : response.data?.results || []
      // Filter out system roles for organization users (non-superusers)
      // Organization users should only see organization roles (Admin, HR Manager, Recruiter, Viewer, etc.)
      const isSuperuser = (currentUser as any)?.is_superuser || false
      const filteredRoles = isSuperuser 
        ? rolesData 
        : rolesData.filter(role => {
            // Include organization roles (role_type='organization' or not set)
            // Exclude system roles (role_type='system' OR is_system_role=true)
            const isSystemRole = role.role_type === 'system' || role.is_system_role === true
            return !isSystemRole
          })
      setRoles(filteredRoles)
    } catch (error) {
      console.error('Error fetching roles:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('users.messages.loadRolesError'),
        duration: 5000
      })
    } finally {
      setFetchingRoles(false)
    }
  }

  const handleRoleSelect = (roleId: number) => {
    setSelectedRole(roleId)
  }

  const handleSave = async () => {
    if (!user) return

    if (!selectedRole) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('users.messages.selectRole', 'Please select a role'),
        duration: 5000
      })
      return
    }

    setLoading(true)
    try {
      await userManagementApi.assignRolesToUser({
        user_id: user.id,
        role_ids: [selectedRole]
      })

      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('users.messages.rolesAssigned'),
        duration: 3000
      })

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error assigning roles:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('users.assignRolesError'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading && !fetchingRoles) {
      onClose()
    }
  }

  if (!user) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${t('users.assignRoles')} - ${user.full_name || user.email}`}
    >
      <div className="space-y-4">
        {/* Current User Info */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">{user.full_name || user.email}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Current Roles */}
        {user.roles.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">{t('users.currentRoles')}</h3>
            <div className="flex flex-wrap gap-2">
              {user.roles.map((userRole) => (
                <Badge 
                  key={userRole.id}
                  style={{ backgroundColor: userRole.role_color, color: 'white' }}
                  className="text-xs"
                >
                  {userRole.role_name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Available Roles */}
        <div>
          <h3 className="text-sm font-medium mb-3">{t('users.availableRoles')}</h3>
          
          {fetchingRoles ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {roles.map((role) => {
                const isSelected = selectedRole === role.id
                const isSystemRole = role.is_system_role
                
                return (
                  <label 
                    key={role.id} 
                    className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                    } ${isSystemRole ? 'opacity-75' : ''}`}
                  >
                    <input
                      type="radio"
                      name="role-selection"
                      checked={isSelected}
                      onChange={() => handleRoleSelect(role.id)}
                      disabled={loading || fetchingRoles}
                      className="rounded"
                    />
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: role.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium">{role.name}</p>
                        {isSystemRole && (
                          <Shield className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {t(`roles.roleDescriptions.${role.name}`, role.description)}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {role.permission_count} {t('roles.permissions')}
                        </span>
                        {!role.is_active && (
                          <Badge variant="secondary" className="text-xs">
                            {t('common.inactive')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </label>
                )
              })}
              
              {roles.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{t('users.noRoles')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected Role Summary */}
        {selectedRole && (
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm font-medium mb-2">
              {t('users.selectedRole', 'Selected Role')}
            </p>
            <div className="flex flex-wrap gap-1">
              {(() => {
                const role = roles.find(r => r.id === selectedRole)
                return role ? (
                  <Badge 
                    key={selectedRole}
                    style={{ backgroundColor: role.color, color: 'white' }}
                    className="text-xs"
                  >
                    {role.name}
                  </Badge>
                ) : null
              })()}
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end space-x-3 mt-6">
        <Button 
          variant="outline" 
          onClick={handleClose}
          disabled={loading || fetchingRoles}
        >
          {t('users.form.cancel')}
        </Button>
        <Button 
          onClick={handleSave}
          disabled={loading || fetchingRoles}
        >
          {loading ? t('common.loading') : t('users.saveRoles')}
        </Button>
      </div>
    </Modal>
  )
}
